// frontend/src/pages/bills/BillsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Tab } from '@headlessui/react';
import { 
  Zap, 
  Tv, 
  Wallet, 
  CheckCircle, 
  AlertCircle, 
  History, 
  ChevronRight,
  Home,
  Wifi,
  BookOpen,
  Shield,
  TrendingUp
} from 'lucide-react';
import ElectricityTab from './components/ElectricityTab';
import TVTab from './components/TVTab';
import TransactionHistory from './components/BillTransactionHistory';
import useBillsStore from '../../store/billsStore';
import useWalletStore from '../../store/walletStore';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const BillsPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  
  const { getTransactionSummary, transactionSummary } = useBillsStore();
  const { wallet, getWallet } = useWalletStore();

  const hasLoadedData = useRef(false);

  useEffect(() => {
    if (!hasLoadedData.current) {
      hasLoadedData.current = true;
      getWallet();
      getTransactionSummary();
    }
  }, [getWallet, getTransactionSummary]);

  const tabs = [
    {
      id: 'electricity',
      name: 'Electricity',
      icon: Zap,
      description: 'Pay electricity bills for all providers',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      id: 'tv',
      name: 'TV Subscription',
      icon: Tv,
      description: 'Subscribe to DSTV, GOTV, Startimes',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-4 md:p-5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 md:p-5 shadow-neomorph mb-3 sm:mb-4 md:mb-5 border border-primary-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">Utility Bills</h1>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 ml-3 hidden sm:block">Pay all your bills in one place</p>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-neomorph-inset border border-primary-100/30">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">Wallet Balance</p>
                <div className="flex items-center">
                  <span className="text-sm sm:text-base md:text-lg font-bold text-gray-800">
                    {formatAmount(wallet?.balance)}
                  </span>
                  <Wallet className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-500 ml-1" />
                </div>
              </div>
              <button
                onClick={() => setShowHistory(true)}
                className="bg-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs sm:text-sm font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <History className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
                <span className="sm:hidden">Hist</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards - Scrollable on mobile */}
        <div className="mb-3 sm:mb-5 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-3 gap-2 sm:gap-4 min-w-[280px] sm:min-w-0">
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs text-gray-600 mb-1">Electricity</p>
                  <p className="text-sm sm:text-base md:text-lg font-bold text-gray-800 truncate">
                    {formatAmount(transactionSummary?.electricity?.totalSpent || 0)}
                  </p>
                  <p className="text-[9px] sm:text-xs text-gray-500 mt-1 truncate">
                    {transactionSummary?.electricity?.transactionCount || 0} payments
                  </p>
                </div>
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md flex-shrink-0 ml-2">
                  <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs text-gray-600 mb-1">TV Subscription</p>
                  <p className="text-sm sm:text-base md:text-lg font-bold text-gray-800 truncate">
                    {formatAmount(transactionSummary?.tv?.totalSpent || 0)}
                  </p>
                  <p className="text-[9px] sm:text-xs text-gray-500 mt-1 truncate">
                    {transactionSummary?.tv?.transactionCount || 0} subscriptions
                  </p>
                </div>
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md flex-shrink-0 ml-2">
                  <Tv className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl p-3 sm:p-5 shadow-neomorph border border-gray-100 mb-3 sm:mb-5">
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <Tab.List className="flex space-x-1 bg-[#E8ECF5] rounded-lg p-1 shadow-neomorph-inset mb-4 sm:mb-5">
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  className={({ selected }) =>
                    classNames(
                      'flex-1 py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center transition-all',
                      selected
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                        : 'text-gray-600 hover:text-gray-800'
                    )
                  }
                >
                  <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">{tab.name}</span>
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <ElectricityTab />
              </Tab.Panel>
              <Tab.Panel>
                <TVTab />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>

        {/* Benefits Section - Scrollable on mobile */}
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-3 gap-2 sm:gap-4 min-w-[280px] sm:min-w-0">
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md shadow-md flex-shrink-0">
                  <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800">Instant Tokens</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                    Receive electricity tokens instantly
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md flex-shrink-0">
                  <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800">Verified Providers</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                    All billers are verified
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md flex-shrink-0">
                  <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800">Secure Payments</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                    Your payments are safe
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History Modal */}
        {showHistory && (
          <TransactionHistory onClose={() => setShowHistory(false)} />
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default BillsPage;